import { BetSlipMode, OddStatus } from "../types/enums";
import { StateActionTypes } from "../types/enums";
import {
  applyRecoveryChange,
  isBetBuilderBetExpired,
  isSelectionExpired,
  resolveUpdatedSelection,
} from "../shared/utils/action-state";
import {
  applyRecoveryChangesToBetBuilder,
  dispatchBetBuilderRemovals,
  updateBetBuilderSelections,
} from "../shared/utils/bet-builder";
import { dispatchCustomEvent } from "../shared/utils/bet-slip";

const removeExpiredBetBuilderEvents = (bets: IBetBuilderBet[]) => {
  const expiredEventIds = [
    ...new Set(bets.filter(isBetBuilderBetExpired).map((bet) => bet.eventId)),
  ];
  expiredEventIds.forEach((eventId) =>
    dispatchCustomEvent(StateActionTypes.REMOVE_BB_EVENT, Number(eventId)),
  );
};

export const oddsChangesHandlers: ReducerHandlers = {
  [StateActionTypes.ACCEPT_ODDS_CHANGES]: (state, action) => {
    // Filter out closed, suspended, and expired (past game time) selections
    const activeSelections = state.betSlipState.selections
      .filter(
        (selection: IBetSlipSelection) =>
          selection.betStatus !== OddStatus.Suspended &&
          selection.betStatus !== OddStatus.Closed &&
          !isSelectionExpired(selection),
      )
      .map((selection: IBetSlipSelection) => ({
        ...selection,
        oldOddPrice: 0, // Clear the old price indicator
      }));

    // Accepting changes also drops any Bet Builder EVENT that suspended, or
    // single LEG whose own odd/bet type suspended/closed, while the user
    // hadn't accepted yet.
    dispatchBetBuilderRemovals(state.betSlipState.betBuilderState.bets);

    removeExpiredBetBuilderEvents(state.betSlipState.betBuilderState.bets);

    // Acknowledge (not apply) any BB price movement: the widget already holds
    // the live price, this only clears the "previouseOdds < odds" signal so the
    // Accept button can be dismissed instead of re-triggering every render.
    const acknowledgedBets = state.betSlipState.betBuilderState.bets.map((bet) =>
      bet.previouseOdds != null ? { ...bet, previouseOdds: bet.odds } : bet,
    );
    const acknowledgedTotalOdds = state.betSlipState.betBuilderState.totalOdds.map(
      (to) => ({ ...to, previouseOdds: to.odd }),
    );

    const newBetSlipState: IBetSlipState = {
      ...state.betSlipState,
      selections: activeSelections,
      betBuilderState: {
        ...state.betSlipState.betBuilderState,
        bets: acknowledgedBets,
        totalOdds: acknowledgedTotalOdds,
      },
      acceptOddsChanges: state.betSlipState.acceptOddsChanges,
      mode:
        activeSelections.length < 2 && !state.betSlipState.betBuilderState.bets.length
          ? BetSlipMode.Single
          : state.betSlipState.mode,
    };

    return {
      ...state,
      betSlipState: newBetSlipState,
    };
  },

  [StateActionTypes.UPDATE_ODDS_CHANGES]: (state, action) => {
    const updatedEvents = action.payload.data || [];

    const newSelections = state.betSlipState.selections.map(
      (selection: IBetSlipSelection) => {
        const updatedEvent = updatedEvents.find(
          (event: IEvent) => event.id === selection.eventId,
        );

        // Event not found in the update, keep selection unchanged
        return updatedEvent
          ? resolveUpdatedSelection(selection, updatedEvent)
          : selection;
      },
    );

    // Closed/Suspended selections stay here regardless of AOC — AOC-off must
    // show the Accept button and wait for the click before removing anything
    // (only the AOC-on branch below is allowed to auto-remove them).
    const newSelectionsWithoutExpired = newSelections.filter(
      (s: IBetSlipSelection) => !isSelectionExpired(s),
    );

    // Handle Bet Builder Selections Updates
    if (state.betSlipState.betBuilderState?.bets?.length > 0) {
      updateBetBuilderSelections(
        state.betSlipState.betBuilderState.bets,
        updatedEvents,
      );
      removeExpiredBetBuilderEvents(state.betSlipState.betBuilderState.bets);
    }

    if (state.betSlipState.acceptOddsChanges) {
      // Auto-accept mode: Filter out suspended/closed selections and clear old price indicators
      const activeSelections = newSelectionsWithoutExpired
        .filter(
          (selection: IBetSlipSelection) =>
            selection.betStatus !== OddStatus.Suspended &&
            selection.betStatus !== OddStatus.Closed,
        )
        .map((selection: IBetSlipSelection) => ({
          ...selection,
          oldOddPrice: 0, // Clear the old price indicator since we're auto-accepting
        }));

      const newBetSlipState: IBetSlipState = {
        ...state.betSlipState,
        selections: activeSelections,
        mode:
          state.betSlipState.selections.length < 2 &&
          !state.betSlipState.betBuilderState.bets.length
            ? BetSlipMode.Single
            : state.betSlipState.mode,
      };
      return {
        ...state,
        betSlipState: newBetSlipState,
      };
    } else {
      // Manual accept mode: Update selections to show changes with oldOddPrice
      const newBetSlipState: IBetSlipState = {
        ...state.betSlipState,
        selections: newSelectionsWithoutExpired,
      };
      return {
        ...state,
        betSlipState: newBetSlipState,
      };
    }
  },

  [StateActionTypes.UPDATE_SELECTIONS_FROM_RECOVERY]: (state, action) => {
    const changes = action.payload || [];
    if (changes.length === 0) {
      return state;
    }

    const newSelections = state.betSlipState.selections.map(
      (selection: IBetSlipSelection) => applyRecoveryChange(selection, changes),
    );

    applyRecoveryChangesToBetBuilder(
      state.betSlipState.betBuilderState.bets,
      changes,
    );

    return {
      ...state,
      betSlipState: { ...state.betSlipState, selections: newSelections },
      error: undefined,
    };
  },
};
